import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { MessageCircle, MessageSquareText, X, Send, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useSite } from '@/context/SiteContext';
import { api, errMsg, waLink } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.61-.91-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.06 2.87 1.21 3.07c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35M12.05 21.78h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.86 9.86 0 0 1-1.51-5.26c0-5.45 4.44-9.88 9.9-9.88 2.64 0 5.12 1.03 6.99 2.9a9.82 9.82 0 0 1 2.89 6.99c0 5.45-4.44 9.88-9.89 9.88m8.41-18.3A11.82 11.82 0 0 0 12.05 0C5.5 0 .16 5.33.16 11.89c0 2.1.55 4.14 1.59 5.94L.06 24l6.31-1.65a11.9 11.9 0 0 0 5.68 1.45h.01c6.55 0 11.89-5.33 11.89-11.89 0-3.18-1.24-6.16-3.49-8.41" />
  </svg>
);

export const SupportFab = () => {
  const { site } = useSite();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const s = site?.settings || {};
  const hasWa = Boolean(s.whatsapp_number);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error('Please add your name and a message.');
      return;
    }
    setBusy(true);
    try {
      await api.post('/public/messages', { ...form, page: pathname });
      setSent(true);
      setForm({ name: '', email: '', phone: '', message: '' });
      toast.success('Message sent. We will be in touch shortly.');
    } catch (err) {
      toast.error(errMsg(err, 'Could not send your message.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="fixed bottom-4 right-4 sm:bottom-5 sm:right-5 z-50 flex flex-col items-end gap-2" data-testid="support-fab-container">
        <AnimatePresence>
          {open && (
            <motion.div
              key="panel"
              initial={{ opacity: 0, y: 10, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.96 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-col items-end gap-2"
              data-testid="support-fab-panel"
            >
              {hasWa && (
                <a
                  href={waLink(s.whatsapp_number, s.whatsapp_greeting)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="support-fab-whatsapp-link"
                  className="inline-flex items-center gap-2.5 h-10 pl-3.5 pr-1.5 rounded-full bg-card border border-border shadow-[var(--shadow-float)] text-sm hover:bg-secondary transition-colors"
                >
                  <span className="font-medium">WhatsApp</span>
                  <span className="h-7 w-7 rounded-full bg-[#25D366] text-white inline-flex items-center justify-center"><WhatsAppIcon className="h-3.5 w-3.5" /></span>
                </a>
              )}
              <button
                type="button"
                onClick={() => { setOpen(false); setSent(false); setFormOpen(true); }}
                data-testid="support-fab-message-button"
                className="inline-flex items-center gap-2.5 h-10 pl-3.5 pr-1.5 rounded-full bg-card border border-border shadow-[var(--shadow-float)] text-sm hover:bg-secondary transition-colors"
              >
                <span className="font-medium">Send a message</span>
                <span className="h-7 w-7 rounded-full bg-[hsl(var(--brand-navy))] text-white inline-flex items-center justify-center"><MessageSquareText className="h-3.5 w-3.5" /></span>
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? 'Close contact options' : 'Contact us'}
          aria-expanded={open}
          data-testid="support-fab"
          className="h-11 w-11 rounded-full bg-[hsl(var(--brand-navy))] text-white shadow-[var(--shadow-float)] ring-1 ring-[hsl(var(--brand-gold))]/70 inline-flex items-center justify-center transition-transform duration-300 motion-safe:hover:scale-105 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[hsl(var(--ring))]/50"
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.span key={open ? 'x' : 'chat'} initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.18 }} className="inline-flex">
              {open ? <X className="h-[18px] w-[18px]" /> : <MessageCircle className="h-[18px] w-[18px]" />}
            </motion.span>
          </AnimatePresence>
        </button>
      </div>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="sm:max-w-md bg-card" data-testid="support-message-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-normal">Send us a message</DialogTitle>
            <DialogDescription>Tell us about your plans and we will come back to you by email or phone.</DialogDescription>
          </DialogHeader>
          {sent ? (
            <div className="py-8 text-center" data-testid="support-message-success">
              <span className="mx-auto h-12 w-12 rounded-full bg-[hsl(var(--brand-teal))] text-white inline-flex items-center justify-center"><Check className="h-6 w-6" /></span>
              <p className="font-display text-xl mt-5">Thank you</p>
              <p className="text-sm text-muted-foreground mt-2">Your message has reached us. We usually respond within a few hours.</p>
              <button type="button" onClick={() => setFormOpen(false)} className="btn-outline mt-6" data-testid="support-message-close-button">Close</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="support-message-form">
              <div className="space-y-1.5">
                <Label htmlFor="msg-name">Name</Label>
                <Input id="msg-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="support-message-name-input" placeholder="Your name" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="msg-email">Email</Label>
                  <Input id="msg-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="support-message-email-input" placeholder="you@email.com" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="msg-phone">Phone / WhatsApp</Label>
                  <Input id="msg-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="support-message-phone-input" placeholder="+62" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="msg-body">Message</Label>
                <Textarea id="msg-body" required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="support-message-text-input" placeholder="Dates, number of guests, questions..." />
              </div>
              <button type="submit" disabled={busy} className="btn-gold w-full disabled:opacity-60" data-testid="support-message-form-submit">
                <Send className="h-4 w-4" /> {busy ? 'Sending...' : 'Send message'}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
